import { useTranslation, useTranslate } from 'module/hooks/use-translation';
import { ReactComponent as ContextIcon } from 'module/ui/assets/svgs/context-icon.svg';
import { AiContextPopover } from 'module/ui/components/molecules/ai-context-popover';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useRef, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const SidePaneContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: var(--scarlet-gray-10);
`;

const StyledBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-05) var(--spacing-06);
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-06);
  flex: 1 0 0;
  align-self: stretch;
  color: var(--text-tertiary);
  font-weight: var(--font-weight-bold);
  text-align: center;
  height: 100%;
  line-height: var(--line-height-03);

  svg {
    color: var(--icon-secondary);
  }
`;

const ContextCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--spacing-04);
  background-color: var(--scarlet-gray-80);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--spacing-03);
  position: relative;
  gap: var(--spacing-03);
`;

const CardTitleWrapper = styled.div<{ isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  position: relative;
  flex-direction: row;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
`;

const IconWrapper = styled.div<{ $isDefault?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 1;

  svg circle {
    fill: ${({ $isDefault }) => ($isDefault ? 'var(--gray-60)' : '')};
  }

  svg path {
    fill: ${({ $isDefault }) => ($isDefault ? 'var(--orange-50)' : '')};
  }
`;

const CardTitle = styled.div`
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-02);
  font-weight: var(--font-weight-semibold);
  flex: 1;
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-04);
`;

const ContentArea = styled.div<{ $isDefault?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding-left: calc(var(--spacing-03) + 12px);
  margin-left: var(--spacing-04);
  border-left: ${({ $isDefault }) =>
    $isDefault ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
`;

const TemplateContextTextWrapper = styled.div`
  position: relative;
`;

const TemplateContextTextArea = styled.textarea<{
  $isDefault?: boolean;
  $hasOverride?: boolean;
  $isExpanded?: boolean;
}>`
  width: 100%;
  color: ${({ $isDefault }) =>
    $isDefault ? 'var(--text-secondary)' : 'var(--text-primary)'};
  font-size: var(--font-size-01);
  line-height: var(--line-height-02);
  border: none;
  background: transparent;
  resize: none;
  padding: 0;
  font-family: inherit;
  white-space: pre-wrap;
  overflow: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'hidden')};
  max-height: ${({ $isExpanded }) =>
    $isExpanded ? 'none' : 'calc(var(--line-height-02) * 5)'};
  text-decoration: ${({ $hasOverride }) =>
    $hasOverride ? 'line-through' : 'none'};

  &:disabled {
    opacity: 1;
    cursor: ${({ $isExpanded }) => ($isExpanded ? 'default' : 'pointer')};
  }

  &:focus {
    outline: none;
  }
`;

const ShadeOverlay = styled.div<{ $isDefault?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(var(--line-height-02) * 2);
  background: ${({ $isDefault }) =>
    $isDefault
      ? 'linear-gradient(to bottom, transparent, var(--scarlet-gray-80))'
      : 'linear-gradient(to bottom, transparent, var(--background-primary))'};
  pointer-events: all;
  cursor: pointer;
`;

const StyledDivider = styled.div`
  height: var(--spacing-04);
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  padding-left: calc(var(--spacing-03) + 12px);
  margin-left: var(--spacing-04);
`;

const TabContainer = styled.div`
  display: flex;
  gap: var(--spacing-02);
  padding: var(--spacing-02);
  background-color: white;
  border-radius: var(--border-radius-small);
  margin-bottom: var(--spacing-04);
  border: 2px solid var(--button-ghost-border);
  box-sizing: border-box;
`;

const Tab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  border: none;
  border-radius: var(--border-radius-small);
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--scarlet-gray-60)' : 'transparent'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--text-selected)' : 'var(--text-secondary)'};
  font-size: var(--font-size-01);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s ease;
`;

interface ExpandableTextAreaProps {
  text: string;
  isDefault?: boolean;
  hasOverride?: boolean;
}

const ExpandableTextArea = ({
  text,
  isDefault,
  hasOverride,
}: ExpandableTextAreaProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lineCount = useMemo(() => {
    return text.split('\n').length;
  }, [text]);

  const needsExpansion = lineCount > 5;

  useEffect(() => {
    if (textareaRef.current && !isExpanded) {
      textareaRef.current.scrollTop = 0;
    }
  }, [isExpanded]);

  const handleExpand = () => {
    setIsExpanded(true);
  };

  return (
    <TemplateContextTextWrapper>
      <TemplateContextTextArea
        ref={textareaRef}
        value={text}
        disabled
        $isDefault={isDefault}
        $hasOverride={hasOverride}
        $isExpanded={isExpanded}
        rows={isExpanded ? lineCount : Math.min(lineCount, 5)}
      />
      {!isExpanded && needsExpansion && (
        <ShadeOverlay $isDefault={isDefault} onClick={handleExpand} />
      )}
    </TemplateContextTextWrapper>
  );
};

interface ItemContextState {
  isEmpty: boolean;
  hasOverride: boolean;
  showAddContext: boolean;
  showOverrideContext: boolean;
  isAddMode: boolean;
  isOverrideMode: boolean;
}

const QUESTION_ANSWER_ITEM_SECTION_TYPE = 'QUESTION_ANSWER_ITEM';
const RL_GROUP_SECTION_TYPE = 'RL_GROUP_SECTION';
const RL_CUSTOM_SECTION_TYPE = 'RL_CUSTOM_SECTION';

/** Walk tiptap JSON to find RL_GROUP_SECTION by id and return its RL_CUSTOM_SECTION children (QUESTION_ANSWER_ITEM). */
const getQuestionAnswerChildrenFromGroupSection = (
  tiptapJson:
    | {
        type?: string;
        content?: Array<{
          type?: string;
          attrs?: Record<string, unknown>;
          content?: unknown[];
        }>;
      }
    | null
    | undefined,
  groupSectionId: string,
): Array<{ id: string; config: Record<string, unknown> }> => {
  const result: Array<{ id: string; config: Record<string, unknown> }> = [];

  const walk = (
    nodes:
      | Array<{
          type?: string;
          attrs?: Record<string, unknown>;
          content?: unknown[];
        }>
      | undefined,
  ) => {
    if (!Array.isArray(nodes)) {
      return;
    }

    for (const node of nodes) {
      if (node.type === RL_GROUP_SECTION_TYPE) {
        const id = (node.attrs?.templateSectionId ??
          node.attrs?.documentSectionId) as string | undefined;

        if (id === groupSectionId && Array.isArray(node.content)) {
          for (const child of node.content as Array<{
            type?: string;
            attrs?: Record<string, unknown>;
          }>) {
            if (
              child.type === RL_CUSTOM_SECTION_TYPE &&
              child.attrs?.sectionType === QUESTION_ANSWER_ITEM_SECTION_TYPE
            ) {
              const childId = (child.attrs?.templateSectionId ??
                child.attrs?.documentSectionId) as string | undefined;
              const config =
                (child.attrs?.config as Record<string, unknown>) || {};

              if (childId) {
                result.push({ id: childId, config });
              }
            }
          }

          return;
        }
      }
      if (Array.isArray(node.content)) {
        walk(
          node.content as Array<{
            type?: string;
            attrs?: Record<string, unknown>;
            content?: unknown[];
          }>,
        );
      }
    }
  };

  walk(tiptapJson?.content as Parameters<typeof walk>[0]);

  return result;
};

const calculateContextState = (
  context: any,
  openStates: { add: boolean; override: boolean } = {
    add: false,
    override: false,
  },
): ItemContextState => {
  const isEmpty =
    !context?.additionalContext || context?.additionalContext === '';
  const hasOverride = !!context?.override;

  const isAddMode = openStates?.add || false;
  const isOverrideMode = openStates?.override || false;

  const showAddContext = (isEmpty || !hasOverride) && !isOverrideMode;
  const showOverrideContext = (isEmpty || hasOverride) && !isAddMode;

  return {
    isEmpty,
    hasOverride,
    isAddMode,
    isOverrideMode,
    showAddContext,
    showOverrideContext,
  };
};

interface RenderContextCardProps {
  itemId: string;
  title: string;
  templateContext?: string;
  emptyMessage: string;
  itemContext: any;
  sectionId: string;
  documentAgentId: string;
  contextState: ItemContextState;
  refetchContexts: () => Promise<any>;
  onOpenStateChange: (
    itemId: string,
    mode: 'add' | 'override',
    open: boolean,
  ) => void;
  useNodeId?: boolean;
  readOnly?: boolean;
}

const renderContextCard = ({
  itemId,
  title,
  templateContext,
  emptyMessage,
  itemContext,
  sectionId,
  documentAgentId,
  contextState,
  refetchContexts,
  onOpenStateChange,
  useNodeId = true,
  readOnly = false,
}: RenderContextCardProps) => {
  const {
    showAddContext,
    showOverrideContext,
    isAddMode,
    isOverrideMode,
    hasOverride,
  } = contextState;

  return (
    <ContextCard key={itemId}>
      <CardWrapper>
        <CardTitleWrapper>
          <IconWrapper $isDefault={true}>
            <ContextIcon width={16} height={16} />
          </IconWrapper>
          <CardTitle>{title}</CardTitle>
        </CardTitleWrapper>

        <ContentArea $isDefault={true}>
          {templateContext ? (
            <ExpandableTextArea
              text={templateContext}
              isDefault={true}
              hasOverride={hasOverride}
            />
          ) : (
            <ExpandableTextArea
              text={emptyMessage}
              isDefault={true}
              hasOverride={hasOverride}
            />
          )}
        </ContentArea>
      </CardWrapper>
      {!readOnly && showAddContext && (
        <CardWrapper>
          <AiContextPopover
            sectionId={sectionId}
            isReadonly={false}
            nodeId={useNodeId ? itemId : undefined}
            documentAgentId={documentAgentId}
            initialContext={itemContext}
            refetchContexts={refetchContexts}
            open={isAddMode}
            mode="add"
            title={<FormattedMessage id="document_agent.preview.add_context" />}
            onOpenChange={(open) => onOpenStateChange(itemId, 'add', open)}
          />
        </CardWrapper>
      )}
      {!readOnly && showAddContext && showOverrideContext && <StyledDivider />}
      {!readOnly && showOverrideContext && (
        <CardWrapper>
          <AiContextPopover
            sectionId={sectionId}
            isReadonly={false}
            nodeId={useNodeId ? itemId : undefined}
            documentAgentId={documentAgentId}
            initialContext={itemContext}
            refetchContexts={refetchContexts}
            open={isOverrideMode}
            mode="override"
            title={
              <FormattedMessage id="document_agent.preview.override_context" />
            }
            onOpenChange={(open) => onOpenStateChange(itemId, 'override', open)}
          />
        </CardWrapper>
      )}
    </ContextCard>
  );
};

export type AIContextSidebarItemContentProps = {
  selectedSection: any;
  template: { tiptapJson?: any } | null | undefined;
  contextItems: any[];
  refetchAIContexts: () => Promise<any>;
  documentationAgentId: number | undefined;
  readOnly?: boolean;
};

export const AIContextSidebarItemContent = ({
  selectedSection,
  template,
  contextItems,
  refetchAIContexts,
  documentationAgentId,
  readOnly = false,
}: AIContextSidebarItemContentProps) => {
  const translate = useTranslate();
  const noContextQuestion = useTranslation(
    'document_agent.preview.no_context_question',
  );
  const noContextTemplate = useTranslation(
    'document_agent.preview.no_context_template',
  );

  const [questionOpenStates, setQuestionOpenStates] = useState<
    Record<string, { add: boolean; override: boolean }>
  >({});
  const [activeTableTab, setActiveTableTab] = useState<'COLUMN' | 'ROW'>(
    'COLUMN',
  );
  const [isAddMode, setIsAddMode] = useState(false);
  const [isOverrideMode, setIsOverrideMode] = useState(false);

  const filteredContextItems = useMemo(() => {
    if (!selectedSection) {
      return [];
    }

    return contextItems.filter((item) => item.id === selectedSection.id);
  }, [contextItems, selectedSection]);

  const groupSectionChildren = useMemo(() => {
    if (
      selectedSection?.type !== RL_GROUP_SECTION_TYPE ||
      !template?.tiptapJson ||
      !selectedSection.id
    ) {
      return [];
    }

    return getQuestionAnswerChildrenFromGroupSection(
      template.tiptapJson as Parameters<
        typeof getQuestionAnswerChildrenFromGroupSection
      >[0],
      selectedSection.id,
    );
  }, [selectedSection?.type, selectedSection?.id, template?.tiptapJson]);

  const isEmpty =
    filteredContextItems[0]?.additionalContext === '' ||
    filteredContextItems.length === 0;
  const hasOverride = !!filteredContextItems[0]?.override;
  const showAddContext = (isEmpty || !hasOverride) && !isOverrideMode;
  const showOverrideContext = (isEmpty || hasOverride) && !isAddMode;

  useEffect(() => {
    setQuestionOpenStates({});
    setIsAddMode(false);
    setIsOverrideMode(false);
  }, [selectedSection?.id]);

  useEffect(() => {
    setQuestionOpenStates({});
  }, [activeTableTab]);

  return (
    <SidePaneContentWrapper>
      <StyledBody>
        {!selectedSection ? (
          <EmptyStateWrapper>
            <FontAwesomeIcon icon={faSparkles} size="2x" />
            <div>
              <FormattedMessage id="document_agent.preview.empty_state" />
            </div>
          </EmptyStateWrapper>
        ) : (
          <>
            {selectedSection?.type === RL_GROUP_SECTION_TYPE ? (
              <>
                {groupSectionChildren.map((child, index) => {
                  const itemContext = contextItems.find(
                    (item) => item.id === child.id,
                  );
                  const contextState = calculateContextState(
                    itemContext,
                    questionOpenStates[child.id],
                  );

                  return renderContextCard({
                    itemId: child.id,
                    title: `${index + 1}. ${(child.config.question as string) ?? ''}`,
                    templateContext: child.config.aiContext as
                      | string
                      | undefined,
                    emptyMessage: noContextQuestion,
                    itemContext,
                    sectionId: child.id,
                    documentAgentId: documentationAgentId?.toString() ?? '',
                    contextState,
                    refetchContexts: refetchAIContexts,
                    onOpenStateChange: (itemId, mode, open) => {
                      setQuestionOpenStates((prev) => ({
                        ...prev,
                        [itemId]: {
                          ...prev[itemId],
                          [mode]: open,
                        },
                      }));
                    },
                    useNodeId: false,
                    readOnly,
                  });
                })}
              </>
            ) : selectedSection?.config?.sectionType === 'MULTI_QUESTION' ? (
              <>
                {selectedSection?.config?.questions?.map(
                  (question: any, index: number) => {
                    const questionId =
                      question.id ||
                      question.nodeId ||
                      question.uuid ||
                      index.toString();

                    const questionContext =
                      filteredContextItems[0]?.internalAiContext?.[questionId];

                    const contextState = calculateContextState(
                      questionContext,
                      questionOpenStates[questionId],
                    );

                    return renderContextCard({
                      itemId: questionId,
                      title: `${index + 1}. ${question?.question}`,
                      templateContext: question?.aiContext,
                      emptyMessage: noContextQuestion,
                      itemContext: questionContext,
                      sectionId: selectedSection.id,
                      documentAgentId: documentationAgentId?.toString() ?? '',
                      contextState,
                      refetchContexts: refetchAIContexts,
                      onOpenStateChange: (itemId, mode, open) => {
                        setQuestionOpenStates((prev) => ({
                          ...prev,
                          [itemId]: {
                            ...prev[itemId],
                            [mode]: open,
                          },
                        }));
                      },
                      readOnly,
                    });
                  },
                )}
              </>
            ) : selectedSection?.config?.sectionType === 'TABLE' ? (
              <>
                <TabContainer>
                  <Tab
                    $isActive={activeTableTab === 'COLUMN'}
                    onClick={() => setActiveTableTab('COLUMN')}
                  >
                    <FormattedMessage id="document_agent.preview.tab.column" />
                  </Tab>
                  <Tab
                    $isActive={activeTableTab === 'ROW'}
                    onClick={() => setActiveTableTab('ROW')}
                  >
                    <FormattedMessage id="document_agent.preview.tab.row" />
                  </Tab>
                </TabContainer>
                {(activeTableTab === 'COLUMN'
                  ? selectedSection?.config?.columnsInfo || []
                  : selectedSection?.config?.rowsInfo || []
                ).map((item: any, index: number) => {
                  const itemId =
                    item.id || item.nodeId || item.uuid || index.toString();

                  const itemContext =
                    filteredContextItems[0]?.internalAiContext?.[itemId];
                  const contextState = calculateContextState(
                    itemContext,
                    questionOpenStates[itemId],
                  );

                  return renderContextCard({
                    itemId,
                    title: item?.name,
                    templateContext: item?.aiContext,
                    emptyMessage: translate(
                      'document_agent.preview.no_context_in_tab',
                      { tab: activeTableTab.toLowerCase() },
                    ),
                    itemContext,
                    sectionId: selectedSection.id,
                    documentAgentId: documentationAgentId?.toString() ?? '',
                    contextState,
                    refetchContexts: refetchAIContexts,
                    onOpenStateChange: (itemId, mode, open) => {
                      setQuestionOpenStates((prev) => ({
                        ...prev,
                        [itemId]: {
                          ...prev[itemId],
                          [mode]: open,
                        },
                      }));
                    },
                    readOnly,
                  });
                })}
              </>
            ) : (
              renderContextCard({
                itemId: selectedSection.id,
                title: selectedSection?.config?.title,
                templateContext: selectedSection?.config?.aiContext,
                emptyMessage: noContextTemplate,
                itemContext: filteredContextItems[0],
                sectionId: selectedSection.id,
                documentAgentId: documentationAgentId?.toString() ?? '',
                contextState: {
                  isEmpty,
                  hasOverride,
                  showAddContext,
                  showOverrideContext,
                  isAddMode,
                  isOverrideMode,
                },
                refetchContexts: refetchAIContexts,
                onOpenStateChange: (_itemId, mode, open) => {
                  if (mode === 'add') {
                    setIsAddMode(open);
                  } else {
                    setIsOverrideMode(open);
                  }
                },
                useNodeId: false,
                readOnly,
              })
            )}
          </>
        )}
      </StyledBody>
    </SidePaneContentWrapper>
  );
};
